import _ from "lodash";
import React from "react";
import styled, { css } from "styled-components";
import SmallTextEditor from "./SmallTextEditor";

import Input from "./CustomInput";
import ColorInput from "./ColorInput";
import Checkbox from "./Checkbox";
import DB_PATH from "../../database/DB_PATH";
import { uploadMedia } from "../../database/media/cloudflareMedia";

export const Wrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
`;

export const Row = styled.div`
  display: flex;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
  align-items: flex-start;

  @media (max-width: 720px) {
    flex-direction: column;
    gap: var(--space-3);
  }
`;

export const RowItem = styled.div`
  flex: 1;
  min-width: 0;
`;

export const ShortRowItem = styled.div`
  flex: 1;
  min-width: 0;
`;

export const Section = styled.div`
  width: 100%;
  margin-top: var(--space-6);
  margin-bottom: var(--space-4);
`;

export const SectionTitle = styled.span`
  display: inline-block;
  font-family: var(--font-display);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  line-height: var(--line-height-tight);
  letter-spacing: -0.01em;
  color: var(--color-text);
`;

export const SectionTitleWrapper = styled.div`
  margin-bottom: var(--space-3);
  padding-bottom: var(--space-2);
  border-bottom: 1px solid var(--color-border-subtle);
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
`;

const FieldLabel = styled.label`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
`;

const selectShellStyles = css<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 var(--space-3);
  background: ${(p) =>
    p.$disabled ? "var(--color-surface-subtle)" : "var(--color-surface)"};
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  transition:
    border-color var(--duration-fast) var(--ease-standard),
    box-shadow var(--duration-fast) var(--ease-standard);
  opacity: ${(p) => (p.$disabled ? 0.7 : 1)};

  &:focus-within {
    border-color: var(--color-accent);
    box-shadow: var(--shadow-focus);
  }
`;

const SelectShell = styled.div<{ $disabled: boolean }>`
  ${selectShellStyles}
`;

const NativeSelect = styled.select`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  color: var(--color-text);
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  padding-right: var(--space-3);

  &:disabled {
    cursor: not-allowed;
    color: var(--color-text-tertiary);
  }
`;

const ChevronIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ color: "var(--color-text-tertiary)", flexShrink: 0 }}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const HelperError = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-xs);
  color: var(--color-danger);
`;

const ImagePreview = styled.div`
  width: 100px;
  height: 100px;
  background: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const HiddenInput = styled.input`
  position: absolute;
  opacity: 0;
  pointer-events: none;
  width: 0;
  height: 0;
`;

const UploadLabel = styled.label<{ $disabled: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  height: 36px;
  padding: 0 var(--space-4);
  background: var(--color-surface);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  font-family: var(--font-sans);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  cursor: ${(p) => (p.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.5 : 1)};
  transition: background var(--duration-fast) var(--ease-standard);
  align-self: flex-start;

  &:hover {
    background: ${(p) =>
      p.$disabled ? "var(--color-surface)" : "var(--color-surface-subtle)"};
  }
`;

const UploadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

export const renderSectionTitle = (title: string): JSX.Element => (
  <SectionTitleWrapper>
    <SectionTitle>{title}</SectionTitle>
  </SectionTitleWrapper>
);

export const getRowItemObject = (
  label: string,
  placeholder: string,
  type: string,
  id: string
): any => ({
  placeholder,
  label,
  type,
  id,
});

type Props = {
  handleChange?: Function;
  handleBlur?: Function;
  touched?: any;
  errors: any;
  values: any;
  mode: string;
  setFieldValue: Function;
  savedField?: any;
};

export const renderRowWithSingleItem = (
  item: any,
  props: Props
): JSX.Element => {
  const { handleChange, handleBlur, touched, errors, values, mode } = props;
  const error = touched[item.id] && (errors as any)[item.id];
  const isDisabled = mode === "detail" || item.disabled;
  const isReadOnly = Boolean(item.readOnly);

  if (item.type == "textarea" || item.editor === "enhanced") {
    return (
      <Row>
        <RowItem>
          <SmallTextEditor
            id={item.id}
            label={item.label}
            value={_.get(values, item.id) || ""}
            disabled={isDisabled}
            placeholder={
              typeof item.placeholder === "string" ? item.placeholder : undefined
            }
            error={error}
            insertableTags={item.insertableTags}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </RowItem>
      </Row>
    );
  }

  if (item.type === "color") {
    return (
      <Row>
        <RowItem>
          <ColorInput
            name={item.id}
            error={error}
            disabled={isDisabled}
            readOnly={isReadOnly}
            value={_.get(values, item.id)}
            onChange={handleChange}
            onBlur={handleBlur}
            label={item.label}
            placeholder={
              typeof item.placeholder === "string" ? item.placeholder : undefined
            }
            id={item.id}
          />
        </RowItem>
      </Row>
    );
  }

  return (
    <Row>
      <RowItem>
        <Input
          name={item.id}
          error={error}
          placeholder={
            typeof item.placeholder == "string" && item.placeholder
          }
          disabled={isDisabled}
          readOnly={isReadOnly}
          value={_.get(values, item.id)}
          endAdornment={item.endAdornment}
          onChange={handleChange}
          label={item.label}
          type={item.type}
          onBlur={handleBlur}
          id={item.id}
        />
      </RowItem>
    </Row>
  );
};

export const renderRowWithSelect = (
  item: any,
  array: any[],
  props: Props
): JSX.Element => {
  const { touched, errors, values, mode, setFieldValue } = props;
  const selectedId = values[item.id] || "";
  const hasError = touched[item.id] && errors[item.id];
  const isDisabled = mode === "detail";

  return (
    <Row>
      <RowItem>
        <FieldGroup>
          {item.label && <FieldLabel htmlFor={item.id}>{item.label}</FieldLabel>}
          <SelectShell $disabled={isDisabled}>
            <NativeSelect
              id={item.id}
              name={item.id}
              disabled={isDisabled}
              value={selectedId}
              onChange={(event) => setFieldValue(item.id, event.target.value)}
            >
              {item.placeholder && (
                <option value="">{item.placeholder}</option>
              )}
              {array.map((option, index) => (
                <option key={index} value={option.id}>
                  {option.fullName || option.title || option.name}
                </option>
              ))}
            </NativeSelect>
            <ChevronIcon />
          </SelectShell>
          {hasError && <HelperError>{String(hasError)}</HelperError>}
        </FieldGroup>
      </RowItem>
    </Row>
  );
};

export const renderRowFileButtonWUpload = (
  item: any,
  props: Props,
  id: string
): JSX.Element => {
  const { values, mode, setFieldValue } = props;
  const imageURL = values[item.id];
  const isDisabled = mode === "detail";

  const handleImageUpload = async (event: any) => {
    const imageFile = event.currentTarget.files[0];
    if (!imageFile) return;

    try {
      const previewURL = URL.createObjectURL(imageFile);
      setFieldValue(item.id, previewURL);

      const uploaded = await uploadMedia({
        body: imageFile,
        kind: "image",
        fileName: imageFile.name,
        firebasePath:
          `${DB_PATH.HABIT}/${DB_PATH.HABIT_ACTION}/${id}/${item.id}/` +
          imageFile.name,
      });
      setFieldValue(item.id, uploaded.url);
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  const inputId = `product-button-file${item.id}`;

  return (
    <Row>
      <ImagePreview>
        {imageURL ? <img src={imageURL} alt="Preview" /> : null}
      </ImagePreview>
      <RowItem>
        <HiddenInput
          accept="image/*"
          id={inputId}
          type="file"
          disabled={isDisabled}
          onChange={handleImageUpload}
        />
        <UploadLabel htmlFor={inputId} $disabled={isDisabled}>
          <UploadIcon /> Upload image
        </UploadLabel>
      </RowItem>
    </Row>
  );
};

export const renderRowFileButton = (
  item: any,
  props: Props
): JSX.Element => {
  const { values, mode, setFieldValue } = props;
  const imageURL = values[item.id];
  const isDisabled = mode === "detail";

  const inputId = `product-button-file${item.id}`;

  return (
    <Row>
      <ImagePreview>
        {imageURL ? <img src={imageURL} alt="Preview" /> : null}
      </ImagePreview>
      <RowItem>
        <HiddenInput
          accept="image/*"
          id={inputId}
          multiple
          type="file"
          disabled={isDisabled}
          onChange={(event) => {
            setFieldValue(
              item.id,
              URL.createObjectURL(event.currentTarget.files[0])
            );
            setFieldValue(item.id, event.currentTarget.files[0]);
          }}
        />
        <UploadLabel htmlFor={inputId} $disabled={isDisabled}>
          <UploadIcon /> Upload image
        </UploadLabel>
      </RowItem>
    </Row>
  );
};

export const renderRowWithTwoItems = (
  firstItem: any,
  secondItem: any,
  props: Props
): any => {
  const { handleChange, handleBlur, touched, errors, values, mode } = props;

  const renderField = (item: any) => {
    const isDisabled = mode === "detail" || item.disabled;
    const error = touched[item.id] && errors[item.id];
    const Field: any = item.type === "color" ? ColorInput : Input;

    return (
      <Field
        name={item.id}
        error={error}
        placeholder={item.placeholder}
        disabled={isDisabled}
        readOnly={Boolean(item.readOnly)}
        value={_.get(values, item.id)}
        onChange={handleChange}
        label={item.label}
        type={item.type}
        onBlur={handleBlur}
        endAdornment={item.endAdornment}
        id={item.id}
      />
    );
  };

  return (
    <Row>
      <RowItem>{renderField(firstItem)}</RowItem>
      <RowItem>{renderField(secondItem)}</RowItem>
    </Row>
  );
};

export const renderRowCheckboxes = (
  firstItem: any,
  secondItem: any,
  props: Props
): any => {
  const { handleChange, touched, errors, values } = props;

  return (
    <Row>
      <RowItem>
        <Checkbox
          error={touched[firstItem.id] && errors[firstItem.id]}
          placeholder={firstItem.placeholder}
          value={_.get(values, firstItem.id)}
          label={firstItem.label}
          onChange={handleChange}
          type={firstItem.type}
          name={firstItem.id}
          id={firstItem.id}
        />
      </RowItem>
      <RowItem>
        <Checkbox
          error={touched[secondItem.id] && errors[secondItem.id]}
          placeholder={secondItem.placeholder}
          value={_.get(values, secondItem.id)}
          label={secondItem.label}
          onChange={handleChange}
          type={secondItem.type}
          name={secondItem.id}
          id={secondItem.id}
        />
      </RowItem>
    </Row>
  );
};

export const renderRowWInputNCheckbox = (
  firstItem: any,
  secondItem: any,
  props: Props
): any => {
  const { handleChange, handleBlur, touched, errors, values, mode } = props;

  return (
    <Row>
      <RowItem>
        <Input
          name={firstItem.id}
          error={touched[firstItem.id] && errors[firstItem.id]}
          placeholder={firstItem.placeholder}
          disabled={mode === "detail"}
          value={_.get(values, firstItem.id)}
          onChange={handleChange}
          label={firstItem.label}
          type={firstItem.type}
          onBlur={handleBlur}
          endAdornment={firstItem.endAdornment}
          id={firstItem.id}
        />
      </RowItem>
      <RowItem>
        <Checkbox
          error={touched[secondItem.id] && errors[secondItem.id]}
          placeholder={secondItem.placeholder}
          value={_.get(values, secondItem.id)}
          label={secondItem.label}
          onChange={handleChange}
          type={secondItem.type}
          name={secondItem.id}
          id={secondItem.id}
        />
      </RowItem>
    </Row>
  );
};

export const renderRowWithSingleCheckbox = (
  item: any,
  props: Props
): any => {
  const { handleChange, touched, errors, values } = props;

  return (
    <Row>
      <RowItem>
        <Checkbox
          error={touched[item.id] && errors[item.id]}
          placeholder={item.placeholder}
          value={_.get(values, item.id)}
          label={item.label}
          onChange={handleChange}
          type={item.type}
          name={item.id}
          id={item.id}
        />
      </RowItem>
    </Row>
  );
};

export const renderTableThreeColumns = (
  tableProps: any,
  id: string,
  firstItem: any,
  secondItem: any,
  thirdItem: any,
  updateFunction: Function,
  props: Props
) => {
  const { handleChange, handleBlur, touched, errors, values, mode } = props;

  return (
    <div>
      <Row>
        {tableProps.titles.map((title: string) => (
          <ShortRowItem key={title}>
            <FieldLabel>{title}</FieldLabel>
          </ShortRowItem>
        ))}
      </Row>
      {values[id].map((value: any, index: number) => (
        <Row key={index}>
          <ShortRowItem>
            <Input
              endAdornment={firstItem.endAdornment}
              error={touched[firstItem.id] && errors[firstItem.id]}
              placeholder={firstItem.placeholder}
              disabled={mode === "detail"}
              value={value[firstItem.id]}
              onChange={(e: any) =>
                updateFunction(e.target.value, index, firstItem.id)
              }
              name={firstItem.id}
              label={firstItem.label}
              type={firstItem.type}
              onBlur={handleBlur}
              id={firstItem.id}
            />
          </ShortRowItem>
          <ShortRowItem>
            <Input
              endAdornment={secondItem.endAdornment}
              error={touched[secondItem.id] && errors[secondItem.id]}
              placeholder={secondItem.placeholder}
              disabled={mode === "detail"}
              value={value[secondItem.id]}
              label={secondItem.label}
              onChange={handleChange}
              name={secondItem.id}
              type={secondItem.type}
              onBlur={handleBlur}
              id={secondItem.id}
            />
          </ShortRowItem>
          <ShortRowItem>
            <Input
              endAdornment={thirdItem.endAdornment}
              error={touched[thirdItem.id] && errors[thirdItem.id]}
              placeholder={thirdItem.placeholder}
              disabled={mode === "detail"}
              value={value[thirdItem.id]}
              label={thirdItem.label}
              onChange={handleChange}
              name={thirdItem.id}
              type={thirdItem.type}
              onBlur={handleBlur}
              id={thirdItem.id}
            />
          </ShortRowItem>
        </Row>
      ))}
    </div>
  );
};

export const handleRepeatedFormValues = (
  originalDataset: Array<any>,
  formValue: any,
  value: any,
  mode: string
): boolean => {
  const datasetEditMode = originalDataset.filter((item) => {
    const valueToCompare = formValue && formValue.toUpperCase();
    return item.toUpperCase() !== valueToCompare;
  });

  const dataset = mode === "create" ? originalDataset : datasetEditMode;

  const itemIndex = dataset.findIndex((descriptionItem) => {
    const valueToCompare = value && value.toUpperCase();
    return descriptionItem.toUpperCase() === valueToCompare;
  });

  return itemIndex < 0;
};
